import { useEffect, useRef, useState } from "react";
import {
  CalendarX,
  Calendar2Week,
  ChevronRight,
  Umbrella,
  CalendarPlus,
} from "react-bootstrap-icons";
import CreateAppointmentModal from "../../modals/CreateAppointmentModal";
import AppointmentDetailsModal from "../../modals/AppointmentDetailsModal";
import { useAuthContext } from "../../hooks/useAuthContext";
import axios from "axios";
import { ApiConnectionString } from "../../constants/ApiConntection";
import LoadingSpinner from "../../components/LoadingSpinner";
import "./CalendarPage.css";
import SubscribeModal from "../../modals/SubscribeModal";

export default function CalendarPage({ profile }) {
  const [appointments, setAppointments] = useState([]);
  const [availability, setAvailability] = useState(null);
  const [holidays, setHolidays] = useState([]);

  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  const [modalStartDate, setModalStartDate] = useState("");
  const [modalStartTime, setModalStartTime] = useState("");
  const [showSubscribeModal, setShowSubscribeModal] = useState(false);

  const [showAppointmentDetailsModal, setShowAppointmentDetailsModal] =
    useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);

  const [expandedSlot, setExpandedSlot] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [err, setErr] = useState("");
  const [view, setView] = useState("day");
  const [selectedDate, setSelectedDate] = useState(new Date());

  const supportingDataLoadedForToken = useRef(null);

  const { user } = useAuthContext();

  const sessionDefaultTime = profile?.sessionDefaultTime || 60;

  const activeSubscription =
    profile?.activeSubscription ?? profile?.ActiveSubscription ?? false;

  const freeTrialExpiresAt =
    profile?.freeTrialExpiresAt ?? profile?.FreeTrialExpiresAt ?? null;

  const freeTrialExpired =
    freeTrialExpiresAt && new Date(freeTrialExpiresAt) < new Date();

  const subscriptionRequired =
    freeTrialExpired && activeSubscription === false;

  const formatDateForApi = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  const getVisibleAppointmentRange = () => {
    let start;
    let end;

    if (view === "day") {
      start = new Date(selectedDate);
      start.setHours(0, 0, 0, 0);

      end = new Date(start);
      end.setDate(end.getDate() + 1);
    }

    if (view === "week") {
      start = new Date(selectedDate);
      start.setHours(0, 0, 0, 0);

      const day = start.getDay();
      const difference = day === 0 ? -6 : 1 - day;

      start.setDate(start.getDate() + difference);

      end = new Date(start);
      end.setDate(end.getDate() + 7);
    }

    if (view === "month") {
      const firstDayOfMonth = new Date(
        selectedDate.getFullYear(),
        selectedDate.getMonth(),
        1
      );

      const day = firstDayOfMonth.getDay();
      const difference = day === 0 ? -6 : 1 - day;

      start = new Date(firstDayOfMonth);
      start.setDate(start.getDate() + difference);
      start.setHours(0, 0, 0, 0);

      end = new Date(start);
      end.setDate(end.getDate() + 42);
    }

    return {
      start: formatDateForApi(start),
      end: formatDateForApi(end),
    };
  };

  const getHolidays = async () => {
    const response = await axios.get(`${ApiConnectionString}/api/holidays`, {
      headers: {
        Authorization: `Bearer ${user.token}`,
      },
    });

    if (response.status === 200) {
      setHolidays(response.data);
    }
  };

  const getAvailability = async () => {
    const response = await axios.get(`${ApiConnectionString}/api/Availablity`, {
      headers: {
        Authorization: `Bearer ${user.token}`,
      },
    });

    if (response.status === 200) {
      setAvailability(response.data);
    }
  };

  const getAppointments = async () => {
    const range = getVisibleAppointmentRange();

    const response = await axios.get(
      `${ApiConnectionString}/api/appointments`,
      {
        params: {
          start: range.start,
          end: range.end,
        },
        headers: {
          Authorization: `Bearer ${user.token}`,
        },
      }
    );

    if (response.status === 200) {
      setAppointments(response.data);
    }
  };

  const loadData = async () => {
    const isFirstLoadForUser =
      supportingDataLoadedForToken.current !== user?.token;

    try {
      if (isFirstLoadForUser) {
        supportingDataLoadedForToken.current = user.token;
        setIsLoading(true);
      }

      setErr("");

      const requests = [getAppointments()];

      if (isFirstLoadForUser) {
        requests.push(getAvailability());
        requests.push(getHolidays());
      }

      await Promise.all(requests);
    } catch (error) {
      console.error(error);
      setErr("Failed to load calendar.");

      if (isFirstLoadForUser) {
        supportingDataLoadedForToken.current = null;
      }
    } finally {
      if (isFirstLoadForUser) {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    if (user?.token) {
      loadData();
    }
  }, [user?.token, view, selectedDate]);

  const formatTime = (dateString) =>
    new Date(dateString).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });

  const formatDateForInput = (date) => {
    const d = new Date(date);

    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  const openAppointmentModal = (date = "", time = "") => {
    if (subscriptionRequired) {
      setExpandedSlot(null);
      setShowSubscribeModal(true);
      return;
    }

    setModalStartDate(date ? formatDateForInput(date) : "");
    setModalStartTime(time || "");
    setExpandedSlot(null);
    setShowAppointmentModal(true);
  };

  const closeAppointmentModal = () => {
    setShowAppointmentModal(false);
    setModalStartDate("");
    setModalStartTime("");
  };

  const openAppointmentDetailsModal = (appointment) => {
    setSelectedAppointment(appointment);
    setExpandedSlot(null);
    setShowAppointmentDetailsModal(true);
  };

  const closeAppointmentDetailsModal = () => {
    setShowAppointmentDetailsModal(false);
    setSelectedAppointment(null);
  };

  const sameDay = (a, b) => {
    const dateA = new Date(a);
    const dateB = new Date(b);

    return (
      dateA.getFullYear() === dateB.getFullYear() &&
      dateA.getMonth() === dateB.getMonth() &&
      dateA.getDate() === dateB.getDate()
    );
  };

  const dateOnly = (date) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d;
  };

  const getHolidayForDate = (date) => {
    const selected = dateOnly(date);

    return holidays.find((holiday) => {
      const start = dateOnly(holiday.startDate);
      const end = dateOnly(holiday.endDate);

      return selected >= start && selected <= end;
    });
  };

  const isHolidayDate = (date) => !!getHolidayForDate(date);

  const dayNameToKey = (date) =>
    date
      .toLocaleDateString("en-GB", {
        weekday: "long",
      })
      .toLowerCase();

  const isUnavailableByAvailability = (date) => {
    if (!availability) return false;

    const key = dayNameToKey(date);

    return (
      !availability[`${key}Available`] ||
      !availability[`${key}StartTime`] ||
      !availability[`${key}EndTime`]
    );
  };

  const getAppointmentName = (appointment) =>
    appointment.therapySession ? appointment.clientName : appointment.name;

  const getAppointmentType = (appointment) =>
    appointment.therapySession ? "Therapy Session" : "Unavailable";

  const getAppointmentColour = (appointment, index) => {
    if (!appointment.therapySession) return "break";

    const colours = ["green", "yellow", "purple", "pink", "blue"];
    return colours[index % colours.length];
  };

  const sortedAppointments = [...appointments].sort(
    (a, b) => new Date(a.start) - new Date(b.start)
  );

  const getAppointmentsForDate = (date) => {
    return sortedAppointments.filter((appointment) =>
      sameDay(appointment.start, date)
    );
  };

  const timeToMinutes = (time) => {
    if (!time) return null;

    const [hours, minutes] = time.split(":").map(Number);
    return hours * 60 + minutes;
  };

  const dateToMinutes = (dateString) => {
    const date = new Date(dateString);
    return date.getHours() * 60 + date.getMinutes();
  };

  const minutesToTime = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;

    return `${String(hours).padStart(2, "0")}:${String(mins).padStart(2, "0")}`;
  };

  const getWorkingRangeForDate = (date) => {
    if (!availability) return null;
    if (isHolidayDate(date)) return null;
    if (isUnavailableByAvailability(date)) return null;

    const key = dayNameToKey(date);

    const startTime = availability[`${key}StartTime`];
    const endTime = availability[`${key}EndTime`];

    if (!startTime || !endTime) return null;

    return {
      start: timeToMinutes(startTime),
      end: timeToMinutes(endTime),
    };
  };

  const getStartOptionsForRange = (startMinutes, endMinutes) => {
    const options = [];

    for (
      let time = startMinutes;
      time + sessionDefaultTime <= endMinutes;
      time += 15
    ) {
      options.push(minutesToTime(time));
    }

    return options;
  };

  const getFreeRangesForDate = (date) => {
    const workingRange = getWorkingRangeForDate(date);

    if (!workingRange) {
      return [];
    }

    const appointmentsForDay = getAppointmentsForDate(date).sort(
      (a, b) => new Date(a.start) - new Date(b.start)
    );

    const freeRanges = [];
    let cursor = workingRange.start;

    appointmentsForDay.forEach((appointment) => {
      const appointmentStart = Math.max(
        dateToMinutes(appointment.start),
        workingRange.start
      );

      const appointmentEnd = Math.min(
        dateToMinutes(appointment.finish),
        workingRange.end
      );

      if (appointmentStart > cursor) {
        const gapLength = appointmentStart - cursor;

        if (gapLength >= sessionDefaultTime) {
          freeRanges.push({
            type: "free",
            startMinutes: cursor,
            endMinutes: appointmentStart,
            start: minutesToTime(cursor),
            end: minutesToTime(appointmentStart),
            options: getStartOptionsForRange(cursor, appointmentStart),
          });
        }
      }

      if (appointmentEnd > cursor) {
        cursor = appointmentEnd;
      }
    });

    if (workingRange.end > cursor) {
      const gapLength = workingRange.end - cursor;

      if (gapLength >= sessionDefaultTime) {
        freeRanges.push({
          type: "free",
          startMinutes: cursor,
          endMinutes: workingRange.end,
          start: minutesToTime(cursor),
          end: minutesToTime(workingRange.end),
          options: getStartOptionsForRange(cursor, workingRange.end),
        });
      }
    }

    return freeRanges;
  };

  const getDayTimelineItems = (date) => {
    const appointmentsForDay = getAppointmentsForDate(date).map(
      (appointment, index) => ({
        type: "appointment",
        startMinutes: dateToMinutes(appointment.start),
        endMinutes: dateToMinutes(appointment.finish),
        appointment,
        colour: getAppointmentColour(appointment, index),
      })
    );

    const freeRanges = getFreeRangesForDate(date);

    return [...appointmentsForDay, ...freeRanges].sort(
      (a, b) => a.startMinutes - b.startMinutes
    );
  };

  const getWeekDates = () => {
    const date = new Date(selectedDate);
    const day = date.getDay();
    const difference = day === 0 ? -6 : 1 - day;

    const monday = new Date(date);
    monday.setDate(date.getDate() + difference);

    return Array.from({ length: 7 }, (_, index) => {
      const weekDate = new Date(monday);
      weekDate.setDate(monday.getDate() + index);
      return weekDate;
    });
  };

  const getMonthDates = () => {
    const year = selectedDate.getFullYear();
    const month = selectedDate.getMonth();

    const firstDay = new Date(year, month, 1);
    const startDay = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1;

    const startDate = new Date(firstDay);
    startDate.setDate(firstDay.getDate() - startDay);

    return Array.from({ length: 42 }, (_, index) => {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + index);
      return date;
    });
  };

  const moveDate = (direction) => {
    const date = new Date(selectedDate);

    if (view === "day") {
      date.setDate(date.getDate() + direction);
    }

    if (view === "week") {
      date.setDate(date.getDate() + direction * 7);
    }

    if (view === "month") {
      date.setMonth(date.getMonth() + direction);
    }

    setExpandedSlot(null);
    setSelectedDate(date);
  };

  const getTitle = () => {
    if (view === "day") {
      return selectedDate.toLocaleDateString([], {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
      });
    }

    if (view === "week") {
      const week = getWeekDates();

      return `${week[0].toLocaleDateString([], {
        day: "numeric",
        month: "short",
      })} - ${week[6].toLocaleDateString([], {
        day: "numeric",
        month: "short",
        year: "numeric",
      })}`;
    }

    return selectedDate.toLocaleDateString([], {
      month: "long",
      year: "numeric",
    });
  };

  if (isLoading) {
    return (
      <div className="calendar-loading">
        <LoadingSpinner />
      </div>
    );
  }

  const dayTimelineItems = getDayTimelineItems(selectedDate);
  const selectedHoliday = getHolidayForDate(selectedDate);
  const selectedDateIsHoliday = isHolidayDate(selectedDate);
  const selectedDateIsUnavailable = isUnavailableByAvailability(selectedDate);

  const hasAppointmentsOnSelectedDate =
    getAppointmentsForDate(selectedDate).length > 0;

  const selectedDateBlocked =
    selectedDateIsHoliday || selectedDateIsUnavailable;

  const showFullUnavailableCard =
    selectedDateBlocked && !hasAppointmentsOnSelectedDate;

    const testRefresh = async () => {
  try {
    const res = await axios.post(
      `${ApiConnectionString}/api/refresh`,
      {},
      {
        withCredentials: true,
      }
    );

    console.log(res.data);
    alert("Refresh worked");
  } catch (error) {
    console.error(error);
    alert("Refresh failed");
  }
};

  return (
    <>
      {profile && (
        <CreateAppointmentModal
          appointments={appointments}
          profile={profile}
          availability={availability}
          show={showAppointmentModal}
          startDate={modalStartDate}
          startTime={modalStartTime}
          onClose={closeAppointmentModal}
          onCreated={() => {
            closeAppointmentModal();
            loadData();
          }}
        />
      )}

      <AppointmentDetailsModal
        show={showAppointmentDetailsModal}
        appointment={selectedAppointment}
        onClose={closeAppointmentDetailsModal}
        onUpdated={() => {
          closeAppointmentDetailsModal();
          loadData();
        }}
        onDeleted={() => {
          closeAppointmentDetailsModal();
          loadData();
        }}
      />

      <SubscribeModal
        show={showSubscribeModal}
        onClose={() => setShowSubscribeModal(false)}
      />

      <div className="calendar-page">
        <div className="calendar-page-header">
          <div>
            <h1>Appointments</h1>
            <p>View your appointments and unavailable time.</p>
          </div>
<button onClick={testRefresh}>
  Test Refresh
</button>
          <button
            className="calendar-create-btn"
            onClick={() => openAppointmentModal()}
          >
            <CalendarPlus />
            <span>New appointment</span>
          </button>
        </div>

        <div className="brand-card calendar-card">
          <div className="calendar-toolbar">
            <div className="d-flex gap-2">
              <button className="small-btn" onClick={() => moveDate(-1)}>
                ‹
              </button>

              <button
                className="small-btn"
                onClick={() => {
                  setExpandedSlot(null);
                  setSelectedDate(new Date());
                }}
              >
                Today
              </button>

              <button className="small-btn" onClick={() => moveDate(1)}>
                ›
              </button>
            </div>

            <h2>{getTitle()}</h2>

            <div className="view-toggle">
              <button
                className={view === "day" ? "active" : ""}
                onClick={() => {
                  setExpandedSlot(null);
                  setView("day");
                }}
              >
                Day
              </button>

              <button
                className={view === "week" ? "active" : ""}
                onClick={() => {
                  setExpandedSlot(null);
                  setView("week");
                }}
              >
                Week
              </button>

              <button
                className={view === "month" ? "active" : ""}
                onClick={() => {
                  setExpandedSlot(null);
                  setView("month");
                }}
              >
                Month
              </button>
            </div>
          </div>

          {err && <div className="alert alert-danger mt-3">{err}</div>}

          {view === "day" && (
            <div className="day-calendar">
              {selectedDateIsHoliday && showFullUnavailableCard && (
                <div className="unavailable-day-card">
                  <div className="unavailable-illustration">
                    <Umbrella />
                  </div>

                  <div className="unavailable-content">
                    <div className="unavailable-icon">
                      <CalendarX />
                    </div>

                    <h3>Unavailable today</h3>

                    <div className="unavailable-pill">
                      Holiday
                      {selectedHoliday?.reason && (
                        <>
                          <span>•</span>
                          {selectedHoliday.reason}
                        </>
                      )}
                    </div>

                    <p>
                      You're marked as unavailable for this day, so no
                      appointments can be booked automatically.
                    </p>

                    <button type="button" className="unavailable-action-btn">
                      Edit holiday
                    </button>
                  </div>
                </div>
              )}

              {!selectedDateIsHoliday &&
                selectedDateIsUnavailable &&
                showFullUnavailableCard && (
                  <div className="unavailable-day-card">
                    <div className="unavailable-illustration">
                      <CalendarX />
                    </div>

                    <div className="unavailable-content">
                      <div className="unavailable-icon">
                        <CalendarX />
                      </div>

                      <h3>Unavailable today</h3>

                      <div className="unavailable-pill">Not working</div>

                      <p>
                        You are not available on this day based on your weekly
                        availability.
                      </p>

                      <button type="button" className="unavailable-action-btn">
                        Edit availability
                      </button>
                    </div>
                  </div>
                )}

              {selectedDateBlocked && hasAppointmentsOnSelectedDate && (
                <div className="unavailable-inline-notice">
                  <CalendarX />

                  <div>
                    <strong>
                      {selectedDateIsHoliday
                        ? "Holiday"
                        : "Normally unavailable"}
                    </strong>

                    <span>
                      Appointments are still shown here because you may choose
                      to override your usual availability.
                    </span>
                  </div>
                </div>
              )}

              {showFullUnavailableCard && (
                <div className="unavailable-week-link">
                  <span></span>

                  <div>
                    <Calendar2Week />
                    <p>Want to see the rest of your week?</p>
                  </div>

                  <button
                    type="button"
                    onClick={() => {
                      setExpandedSlot(null);
                      setView("week");
                    }}
                  >
                    View week <ChevronRight />
                  </button>

                  <span></span>
                </div>
              )}

              {!selectedDateBlocked && dayTimelineItems.length === 0 && (
                <div className="empty-calendar">
                  <h3>No available gaps</h3>

                  <p>
                    There are no free spaces long enough for a{" "}
                    {sessionDefaultTime}-minute appointment.
                  </p>
                </div>
              )}

              {dayTimelineItems.map((item) => {
                if (item.type === "appointment") {
                  const appointment = item.appointment;
                  const startTime = formatTime(appointment.start);
                  const finishTime = formatTime(appointment.finish);

                  return (
                    <div
                      className="calendar-row"
                      key={`appointment-${appointment.id || appointment.Id}`}
                    >
                      <div className="calendar-time">{startTime}</div>

                      <div
                        className={`appointment-block ${item.colour}`}
                        role="button"
                        tabIndex={0}
                        onClick={() =>
                          openAppointmentDetailsModal(appointment)
                        }
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            openAppointmentDetailsModal(appointment);
                          }
                        }}
                      >
                        <div>
                          <span>
                            {startTime} – {finishTime}
                          </span>

                          <strong>{getAppointmentName(appointment)}</strong>
                          <small>{getAppointmentType(appointment)}</small>
                        </div>

                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            openAppointmentDetailsModal(appointment);
                          }}
                        >
                          ⋮
                        </button>
                      </div>
                    </div>
                  );
                }

                if (item.type === "free" && selectedDateBlocked) {
                  return null;
                }

                const slotKey = `${selectedDate.toDateString()}-${item.start}`;

                return (
                  <div className="calendar-row" key={slotKey}>
                    <div className="calendar-time">{item.start}</div>

                    <div className="appointment-block free">
                      <button
                        type="button"
                        className="free-block-button"
                        onClick={() =>
                          setExpandedSlot(
                            expandedSlot === slotKey ? null : slotKey
                          )
                        }
                      >
                        <div>
                          <span>
                            {item.start} – {item.end}
                          </span>

                          <strong>Free</strong>

                          <small>
                            {item.options.length} available start time
                            {item.options.length !== 1 ? "s" : ""}
                          </small>
                        </div>

                        <span>⌄</span>
                      </button>

                      {expandedSlot === slotKey && (
                        <div className="slot-options">
                          {item.options.map((time) => (
                            <button
                              type="button"
                              key={time}
                              onClick={() =>
                                openAppointmentModal(selectedDate, time)
                              }
                            >
                              {time}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {view === "week" && (
            <div className="week-calendar">
              {getWeekDates().map((date) => {
                const appointmentsForDay = getAppointmentsForDate(date);
                const freeRanges = getFreeRangesForDate(date);
                const holiday = getHolidayForDate(date);
                const unavailable = isUnavailableByAvailability(date);

                return (
                  <div
                    className={`week-day ${
                      holiday || unavailable ? "holiday-day" : ""
                    }`}
                    key={date.toISOString()}
                  >
                    <div className="week-day-header">
                      <strong>
                        {date.toLocaleDateString([], {
                          weekday: "short",
                        })}
                      </strong>

                      <span>{date.getDate()}</span>
                    </div>

                    {holiday && (
                      <p className="week-holiday">
                        Holiday{holiday.reason ? `: ${holiday.reason}` : ""}
                      </p>
                    )}

                    {!holiday && unavailable && (
                      <p className="week-unavailable">Unavailable</p>
                    )}

                    {(holiday || unavailable) &&
                      appointmentsForDay.length === 0 && (
                        <p className="week-empty">No appointments</p>
                      )}

                    {getDayTimelineItems(date).map((item) => {
                      if (item.type === "appointment") {
                        const appointment = item.appointment;

                        return (
                          <div
                            className={`week-appointment ${item.colour}`}
                            key={`week-appointment-${
                              appointment.id || appointment.Id
                            }`}
                            role="button"
                            tabIndex={0}
                            onClick={() =>
                              openAppointmentDetailsModal(appointment)
                            }
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                openAppointmentDetailsModal(appointment);
                              }
                            }}
                          >
                            <small>
                              {formatTime(appointment.start)} –{" "}
                              {formatTime(appointment.finish)}
                            </small>

                            <strong>{getAppointmentName(appointment)}</strong>
                          </div>
                        );
                      }

                      if (holiday || unavailable) {
                        return null;
                      }

                      return (
                        <button
                          type="button"
                          className="week-free-slot"
                          key={`${date.toISOString()}-${item.start}`}
                          onClick={() => {
                            setSelectedDate(date);
                            setExpandedSlot(
                              `${date.toDateString()}-${item.start}`
                            );
                            setView("day");
                          }}
                        >
                          <small>
                            Free {item.start} – {item.end}
                          </small>
                        </button>
                      );
                    })}

                    {!holiday &&
                      !unavailable &&
                      appointmentsForDay.length === 0 &&
                      freeRanges.length === 0 && (
                        <p className="week-empty">Fully booked</p>
                      )}
                  </div>
                );
              })}
            </div>
          )}

          {view === "month" && (
            <div className="month-calendar">
              {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((day) => (
                <div className="month-heading" key={day}>
                  {day}
                </div>
              ))}

              {getMonthDates().map((date) => {
                const appointmentsForDay = getAppointmentsForDate(date);
                const freeRanges = getFreeRangesForDate(date);
                const holiday = getHolidayForDate(date);
                const unavailable = isUnavailableByAvailability(date);

                const isOtherMonth =
                  date.getMonth() !== selectedDate.getMonth();

                const startOptionsCount = freeRanges.reduce(
                  (total, range) => total + range.options.length,
                  0
                );

                return (
                  <button
                    type="button"
                    className={`month-day ${
                      isOtherMonth ? "other-month" : ""
                    } ${holiday || unavailable ? "holiday-day" : ""}`}
                    key={date.toISOString()}
                    onClick={() => {
                      setExpandedSlot(null);
                      setSelectedDate(date);
                      setView("day");
                    }}
                  >
                    <span>{date.getDate()}</span>

                    {holiday && <small>Holiday</small>}

                    {!holiday && unavailable && <small>Unavailable</small>}

                    {appointmentsForDay.length > 0 && (
                      <small>
                        {appointmentsForDay.length} appointment
                        {appointmentsForDay.length > 1 ? "s" : ""}
                      </small>
                    )}

                    {!holiday && !unavailable && startOptionsCount > 0 && (
                      <small>{startOptionsCount} start times</small>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
}